import {
  ReactNode,
  RefObject,
  InputHTMLAttributes,
  useCallback,
  useState,
  useId,
  forwardRef,
  useRef,
} from 'react';
import { IconName, IconSize } from '@ardoq/icons';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import {
  InputStyleProps,
  InputWrapper,
  PresetIcon,
  FormErrorIcon,
  FormWarningIcon,
} from '../../atoms';
import { Field } from '../Field/Field';
import { CommonFieldProps } from '../Field/types';
import { ClickableIcon, PostfixMsg, PrefixMsg, TextInputStyle } from './atoms';
import { getChildElementId } from '../../utils';

type RightIconProps = {
  iconName?: IconName;
  onClick?: VoidFunction;
  tooltip?: string;
  isDisabled?: boolean;
  showClearIcon?: boolean;
  onClear?: VoidFunction;
  i18nLocale: I18nLocale;
};

const RightIcon = ({
  iconName,
  onClick,
  tooltip,
  isDisabled,
  showClearIcon,
  onClear,
  i18nLocale,
}: RightIconProps) => {
  if (showClearIcon)
    return (
      <ClickableIcon
        aria-label={t(
          i18nLocale,
          'forms:textInput.clearInputAriaLabel',
          'Clear input'
        )}
        deprecated_onClick={() => {
          onClear?.();
        }}
        $isDisabled={isDisabled}
        iconName={IconName.CLOSE}
        data-tooltip-text={t(
          i18nLocale,
          'forms:textInput.clearTooltip',
          'Clear'
        )}
        iconSize={IconSize.MEDIUM}
      />
    );

  if (iconName && onClick)
    return (
      <ClickableIcon
        $isDisabled={isDisabled}
        iconName={iconName}
        deprecated_onClick={onClick}
        data-tooltip-text={tooltip}
        iconSize={IconSize.MEDIUM}
      />
    );

  if (iconName)
    return (
      <PresetIcon
        iconName={iconName}
        $isDisabled={isDisabled}
        data-tooltip-text={tooltip}
        iconSize={IconSize.MEDIUM}
      />
    );

  return null;
};

export type TextInputProps = CommonFieldProps &
  Pick<
    InputHTMLAttributes<HTMLInputElement>,
    | 'onClick'
    | 'onBlur'
    | 'onChange'
    | 'onKeyDown'
    | 'value'
    | 'placeholder'
    | 'defaultValue'
    | 'name'
    | 'type'
    | 'form'
    | 'autoComplete'
    | 'autoFocus'
    | 'onFocus'
    | 'className'
    | 'tabIndex'
    | 'aria-label'
  > & {
    leftIconName?: IconName;
    rightIconName?: IconName;
    rightIconOnClick?: VoidFunction;
    rightIconTooltip?: string;
    isReadOnly?: boolean;
    hideErrorIcon?: boolean;
    hideWarningIcon?: boolean;
    alignInputText?: 'left' | 'center' | 'right';
    ref?: RefObject<HTMLInputElement>;
    displayValue?: string;
    id?: string;
    prefix?: string;
    prefixNode?: ReactNode;
    postfix?: string;
    postfixNode?: ReactNode;
    onClear?: VoidFunction;
    /**
     * This will be called when the input is blurred and the value has changed.
     */
    onCommit?: (value: string) => void;
    onValueChange?: (value: string) => void;
    hasTransparentBackground?: boolean;
    hasRightPadding?: boolean;
    hasClearButton?: boolean;
    /**
     * Optional locale for translating the package's internal clear-button
     * `aria-label` and tooltip. When omitted, falls back to English so callers
     * that have not yet adopted the i18n flow remain unchanged.
     */
    i18nLocale?: I18nLocale;
  };

const getAutoCompleteProps = (props: TextInputProps) => {
  if (props.autoComplete === 'off') {
    return {
      'data-1p-ignore': true, // 1Password autocomplete
      'data-lpignore': true, // LastPass autocomplete
      'data-form-type': 'other', // Dashlane autocomplete
    };
  }
};

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      leftIconName,
      rightIconName,
      rightIconOnClick,
      rightIconTooltip,
      onValueChange,
      errorMessage,
      warningMessage,
      helperText,
      hintMessage,
      isDisabled,
      isReadOnly,
      label,
      value,
      hideErrorIcon,
      hideWarningIcon,
      alignInputText = 'left',
      displayValue,
      indicator,
      formSize,
      prefix,
      prefixNode,
      postfix,
      postfixNode,
      onCloseFieldClick,
      closeFieldIconTooltip,
      __DS_ONLY_fieldStyle,
      dataTestId,
      popoverContent,
      popoverHelpContent,
      hasError: hasErrorProp,
      hasWarning: hasWarningProp,
      className,
      onClear,
      onCommit,
      hasTransparentBackground = false,
      hasRightPadding = false,
      hasClearButton = false,
      i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
      ...props
    },
    ref
  ) => {
    const [isDisplayMode, setDisplayMode] = useState(Boolean(displayValue));

    const isCommitedRef = useRef(false);

    const generatedTextInputId = useId();
    const id = props.id ?? generatedTextInputId;

    const hasError = hasErrorProp || Boolean(errorMessage);
    const hasWarning = !hasError && (hasWarningProp || Boolean(warningMessage));
    const hasPrefix = Boolean(prefix || prefixNode);
    const hasPostfix = Boolean(postfix || postfixNode);
    const showClearIcon = Boolean(value) && Boolean(onClear);

    const autoCompleteProps = getAutoCompleteProps(props);

    const styleProps: InputStyleProps = {
      $isDisabled: isDisabled || isReadOnly,
      $hasError: hasError,
      $hasWarning: hasWarning,
      $hasLeftIcon: Boolean(leftIconName),
      $hasRightIcon: Boolean(rightIconName || showClearIcon || hasRightPadding),
      $alignText: alignInputText,
      $hideErrorIcon: hideErrorIcon,
      $hideWarningIcon: hideWarningIcon,
      $hasPrefix: hasPrefix,
      $hasPostfix: hasPostfix,
      $hasTransparentBackground: hasTransparentBackground,
      $hasClearButton: hasClearButton,
    };

    const handleChange = useCallback(
      (newValue: string) => {
        onValueChange?.(newValue);
        isCommitedRef.current = true;
      },
      [onValueChange]
    );

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      props.onBlur?.(event);
      if (isCommitedRef.current && onCommit) {
        onCommit(event.target.value);
        isCommitedRef.current = false;
      }

      if (displayValue === undefined) {
        return;
      }

      setDisplayMode(true);
    };

    return (
      <Field
        label={label}
        inputId={id}
        indicator={indicator}
        helperText={helperText}
        isDisabled={isDisabled}
        formSize={formSize}
        onCloseFieldClick={onCloseFieldClick}
        closeFieldIconTooltip={closeFieldIconTooltip}
        hintMessage={hintMessage}
        errorMessage={errorMessage}
        i18nLocale={i18nLocale}
        warningMessage={warningMessage}
        __DS_ONLY_fieldStyle={__DS_ONLY_fieldStyle}
        dataTestId={dataTestId}
        popoverContent={popoverContent}
        popoverHelpContent={popoverHelpContent}
        className={className}
      >
        <InputWrapper {...styleProps}>
          {prefixNode}
          {prefix && <PrefixMsg {...styleProps}>{prefix}</PrefixMsg>}
          {leftIconName && (
            <PresetIcon
              {...styleProps}
              iconName={leftIconName}
              iconSize={IconSize.MEDIUM}
            />
          )}
          {isDisplayMode ? (
            <TextInputStyle
              id={id}
              ref={ref}
              {...props}
              {...styleProps}
              value={displayValue}
              readOnly={isReadOnly}
              onChange={() => {}}
              onFocus={() => setDisplayMode(false)}
            />
          ) : (
            <TextInputStyle
              {...props}
              {...autoCompleteProps}
              {...styleProps}
              id={id}
              ref={ref}
              value={value}
              readOnly={isReadOnly}
              aria-describedby={
                helperText ? getChildElementId(id, 'help-text') : undefined
              }
              aria-errormessage={
                hasError ? getChildElementId(id, 'error') : undefined
              }
              aria-invalid={hasError ? 'true' : undefined}
              aria-required={
                props.isRequired || indicator === 'required'
                  ? 'true'
                  : undefined
              }
              onChange={e => {
                const newValue = e.currentTarget.value;
                handleChange(newValue);
                props.onChange?.(e);
              }}
              onBlur={handleBlur}
            />
          )}

          {!showClearIcon && hasError && !hideErrorIcon && (
            <FormErrorIcon iconSize={IconSize.MEDIUM} />
          )}
          {!showClearIcon && !hasError && hasWarning && !hideWarningIcon && (
            <FormWarningIcon iconSize={IconSize.MEDIUM} />
          )}
          <RightIcon
            iconName={rightIconName}
            onClick={rightIconOnClick}
            tooltip={rightIconTooltip}
            showClearIcon={showClearIcon}
            isDisabled={isDisabled}
            onClear={onClear}
            i18nLocale={i18nLocale}
          />
          {postfix && <PostfixMsg {...styleProps}>{postfix}</PostfixMsg>}
          {postfixNode}
        </InputWrapper>
      </Field>
    );
  }
);
